import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { flushSync } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useUser } from '../contexts/UserContext';
import { useMenu } from '../contexts/MenuContext';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useRipple } from '../hooks/useRipple';
import { motion } from 'framer-motion';

type SidebarProps = {
  children?: React.ReactNode;
  isBackToTopVisible?: boolean;
};

// Memoized Navigation Item Component
const NavigationItem = memo(({ 
  item, 
  isActive, 
  onNavigate, 
  ripples, 
  sidebarOpen, 
  isMobile, 
  showSidebarText 
}: {
  item: any;
  isActive: boolean;
  onNavigate: (id: string, path: string) => (event: React.MouseEvent) => void;
  ripples: any;
  sidebarOpen: boolean;
  isMobile: boolean;
  showSidebarText: boolean;
}) => (
  <li>
    <button
      onClick={onNavigate(item.id, item.path)}
      className={`group relative flex items-center w-full rounded-xl ${isMobile ? '' : 'transition-colors duration-200'} overflow-hidden ${
        isActive 
          ? 'border border-blue-200 dark:border-blue-700 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 text-blue-700 dark:text-blue-300 shadow-sm' 
          : `border ${isMobile ? 'border-gray-300 dark:border-gray-600' : 'border-gray-200 dark:border-gray-700'} bg-gradient-to-br ${isMobile ? 'from-gray-50 to-gray-150' : 'from-white to-gray-100'} dark:from-gray-800 dark:to-gray-700 text-gray-700 dark:text-gray-300 hover:from-blue-50 hover:to-indigo-100 dark:hover:from-blue-900/20 dark:hover:to-indigo-900/20 hover:text-blue-700 dark:hover:text-blue-300 hover:border-blue-200 dark:hover:border-blue-700 shadow-sm hover:shadow-md transition-all duration-200`
      } text-left`}
    >
      <div className={`rounded-xl ${sidebarOpen || isMobile ? 'p-3' : 'p-2 justify-center'} w-full flex items-center ${
        isActive 
          ? 'border border-white dark:border-gray-700' 
          : 'border border-white dark:border-gray-700'
      }`}>
      <div className={`flex-shrink-0 p-1.5 rounded-lg ${isMobile ? '' : 'transition-colors duration-200'} ${
        isActive 
          ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white' 
          : `bg-gradient-to-br ${isMobile ? 'from-gray-100 to-gray-200' : 'from-gray-100 to-gray-200'} dark:from-gray-700 dark:to-gray-600 text-gray-600 dark:text-gray-400 group-hover:from-blue-500 group-hover:to-indigo-600 group-hover:text-white`
      }`}>
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={item.iconPath} />
        </svg>
      </div>
      <span className={`
        ${((sidebarOpen && showSidebarText) || isMobile) ? 'opacity-100 translate-x-0 w-auto ml-3' : 'opacity-0 -translate-x-2 w-0 overflow-hidden ml-0'} 
        ${isMobile ? '' : 'transition-[opacity,transform,width,margin] duration-300 ease-in-out'} whitespace-nowrap font-medium
      `}>
        {item.label}
      </span>
      {((sidebarOpen && showSidebarText) || isMobile) && (
        <div className={`flex-shrink-0 ml-auto ${isMobile ? '' : 'transition-colors duration-200'} ${
          isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400'
        }`}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </div>
      )}
      
      {/* Ripple Effects */}
      {ripples[item.id]?.map((ripple: any) => (
        <motion.div
          key={ripple.id}
          className="absolute rounded-full bg-blue-500/20 pointer-events-none"
          style={{
            left: ripple.x - (ripple.size || 50) / 2,
            top: ripple.y - (ripple.size || 50) / 2,
            width: ripple.size || 50,
            height: ripple.size || 50,
          }}
          initial={{ scale: 0.1, opacity: 0.6 }}
          animate={{ scale: 1, opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        />
      ))}
      </div>
    </button>
  </li>
));

export default function Sidebar({ children, isBackToTopVisible = false }: SidebarProps) {
  const { userData, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { adapt } = useUser();
  const { setIsMenuButtonVisible, isSidebarOpen, setIsSidebarOpen, isMobile, setIsMobile } = useMenu();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Her sayfa değişiminde bileşeni tamamen yeniden render etmek için key
  const sidebarKey = location.key;
  
  // Consolidated UI state
  const [uiState, setUiState] = useState(() => {
    const mobile = window.innerWidth < 768;
    const compact = window.innerWidth <= 820;
    return {
      isMobile: mobile,
      sidebarOpen: !compact,
      showSidebarText: !compact,
      showMenuButton: true,
      showOverlay: false
    };
  });
  
  // Consolidated scroll state
  const [scrollState, setScrollState] = useState({
    shouldHaveBottomPadding: false,
    lastKnownScrollPosition: 0
  });
  
  // Consolidated navigation state - her sayfa değişiminde sıfırlanır
  const [navState, setNavState] = useState(() => ({
    clickedPath: null as string | null,
    modalTransitionTimeout: null as NodeJS.Timeout | null
  }));
  
  const lastScrollTop = useRef(0);
  const { ripples, createRipple } = useRipple<string>();

  // Context'i başlangıçta senkronize et
  useEffect(() => {
    setIsSidebarOpen(uiState.sidebarOpen);
    setIsMobile(uiState.isMobile);
  }, []); // Sadece mount'ta çalışsın

  // uiState değiştiğinde context'i güncelle
  useEffect(() => {
    setIsSidebarOpen(uiState.sidebarOpen);
    setIsMobile(uiState.isMobile);
  }, [uiState.sidebarOpen, uiState.isMobile, setIsSidebarOpen, setIsMobile]);

  // Memoized navigation items
  const navigationItems = useMemo(() => [
    {
      id: 'home',
      path: '/',
      label: 'Ana Sayfa',
      iconPath: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6'
    },
    {
      id: 'account',
      path: '/account',
      label: 'Hesabım',
      iconPath: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
    },
    {
      id: 'warranties',
      path: '/warranties',
      label: 'Garantilerim',
      iconPath: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622C17.176 19.29 21 14.591 21 9a12.02 12.02 0 00-.382-2.016z'
    },
    {
      id: 'service-requests',
      path: '/service-requests',
      label: adapt("Taleplerim"),
      iconPath: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z'
    },
    {
      id: 'orders',
      path: '/orders',
      label: 'Siparişlerim',
      iconPath: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
    }
  ], [adapt]);

  // Optimized handlers
  const handleResize = useCallback(() => {
      const mobile = window.innerWidth < 768;
      const compact = window.innerWidth <= 820;
      const newSidebarOpen = !compact;
      
    setUiState(prev => ({
      ...prev,
      isMobile: mobile,
      sidebarOpen: newSidebarOpen,
      showSidebarText: !compact
    }));
    
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  }, []);

  const handleNavigation = useCallback((id: string, path: string) => {
    return (event: React.MouseEvent) => {
      // Hemen aktif duruma geç ve ripple efektini başlat
      flushSync(() => {
        setNavState(prev => ({ ...prev, clickedPath: path }));
      });
      
      // Ripple efektini hemen başlat (callback olmadan)
      createRipple(event, id);
      
      // Navigation'ı biraz geciktir ki görsel güncelleme görülsün
      setTimeout(() => {
        if (uiState.isMobile) {
          setUiState(prev => ({ ...prev, sidebarOpen: false }));
        }
        navigate(path);
      }, 100);
    };
  }, [createRipple, navigate, uiState.isMobile]);

  const handleLogout = useCallback(() => {
    navigate('/login', { replace: true });
    logout().catch(console.error);
  }, [navigate, logout]);

  const handleSidebarToggle = useCallback(() => {
    // Modal açıkken veya yükleme sırasında sidebar açılmasını engelle
    const isModalOpen = document.body.hasAttribute('data-modal-open');
    const isUploading = document.body.hasAttribute('data-uploading');
    if (isModalOpen || isUploading) return;
    
    setUiState(prev => ({ ...prev, sidebarOpen: !prev.sidebarOpen }));
  }, []);

  const handleThemeToggle = useCallback(() => {
    return (event: React.MouseEvent) => {
      createRipple(event, 'theme', toggleTheme);
    };
  }, [createRipple, toggleTheme]);

  const handleLogoutClick = useCallback(() => {
    return (event: React.MouseEvent) => {
      createRipple(event, 'logout', handleLogout);
    };
  }, [createRipple, handleLogout]);

  const isActive = useCallback((path: string) => {
    // Eğer bir düğmeye tıklandıysa, o düğme aktif olmalı
    if (navState.clickedPath) {
      return navState.clickedPath === path;
    }
    // Tıklanan düğme yoksa, mevcut route'a göre aktif durumu belirle
    return location.pathname === path;
  }, [navState.clickedPath, location.pathname]);

  // Opak zemin: backdrop-filter kayış bitince arkadaki sayfayı bir anlığına beyaza boyuyor.
  const sidebarClassName = useMemo(() => {
    const placement = uiState.isMobile
      ? `fixed left-0 w-72 h-screen-mobile sidebar-drawer ${uiState.sidebarOpen ? 'is-open' : ''}`
      : `sticky top-0 h-screen-mobile ${uiState.sidebarOpen ? 'w-72' : 'w-20'} transition-[width] duration-300 ease-in-out`;
    const border = uiState.isMobile
      ? 'border-white/50 dark:border-gray-700/40'
      : 'border-gray-200/80 dark:border-gray-600/60';

    return `${placement} bg-white dark:bg-gray-900 shadow-2xl shadow-gray-900/10 dark:shadow-black/30 border-r ${border} flex flex-col z-20`;
  }, [uiState.sidebarOpen, uiState.isMobile]);

  const menuButtonClassName = useMemo(() => 
    `fixed top-4 left-4 p-2 rounded-lg bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl text-gray-800 dark:text-gray-200 shadow-lg shadow-gray-900/10 dark:shadow-black/25 border border-gray-200/70 dark:border-gray-600/70 z-20 transform transition-[transform,opacity] duration-150 ${uiState.showMenuButton ? 'translate-y-0 opacity-100' : '-translate-y-16 opacity-0'}`,
  [uiState.showMenuButton]);

  // Ekran boyutunu kontrol et ve viewport height'ı ayarla
  useEffect(() => {
    // İlk yüklemede çalıştır
    handleResize();
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [handleResize]);

  // Optimized scroll handler
  const handleScroll = useCallback(() => {
      const st = window.pageYOffset || document.documentElement.scrollTop;
    const threshold = Math.max(100, window.innerHeight * 0.15);
    const backToTopThreshold = 300;
      
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      const isUploading = document.body.hasAttribute('data-uploading');
      
      if (!isModalOpen && !isUploading) {
      setScrollState(prev => ({
        lastKnownScrollPosition: st,
        shouldHaveBottomPadding: st > backToTopThreshold
      }));
      } else {
      setScrollState(prev => ({
        ...prev,
        shouldHaveBottomPadding: prev.lastKnownScrollPosition > backToTopThreshold
      }));
    }
    
    if (uiState.isMobile) {
        if (isModalOpen || isUploading) {
        setUiState(prev => ({ ...prev, showMenuButton: false }));
          return;
        }
        
        if (st <= 10) {
        setUiState(prev => ({ ...prev, showMenuButton: true }));
      } else if (st > lastScrollTop.current + 10) {
        setUiState(prev => ({ ...prev, showMenuButton: false }));
      } else if (st < lastScrollTop.current - 10 && st < threshold) {
        setUiState(prev => ({ ...prev, showMenuButton: true }));
      }
    }
    
      lastScrollTop.current = st <= 0 ? 0 : st;
  }, [uiState.isMobile]);

    // Modal durumunu kontrol eden observer
  const checkModalStatus = useCallback(() => {
      const isModalOpen = document.body.hasAttribute('data-modal-open');
      const isUploading = document.body.hasAttribute('data-uploading');
      const st = window.pageYOffset || document.documentElement.scrollTop;
      const threshold = Math.max(100, window.innerHeight * 0.15);
      const backToTopThreshold = 300;
      
      if (isModalOpen || isUploading) {
      if (navState.modalTransitionTimeout) {
        clearTimeout(navState.modalTransitionTimeout);
        setNavState(prev => ({ ...prev, modalTransitionTimeout: null }));
      }
      
      setUiState(prev => ({ ...prev, showMenuButton: false }));
      setScrollState(prev => ({ 
        ...prev, 
        shouldHaveBottomPadding: prev.lastKnownScrollPosition > backToTopThreshold 
      }));
      } else {
        const timeoutId = setTimeout(() => {
        setScrollState({
          lastKnownScrollPosition: st,
          shouldHaveBottomPadding: st > backToTopThreshold
        });
        
        setUiState(prev => ({ 
          ...prev, 
          showMenuButton: st <= threshold 
        }));
        
        setNavState(prev => ({ ...prev, modalTransitionTimeout: null }));
      }, 150);
      
      setNavState(prev => ({ ...prev, modalTransitionTimeout: timeoutId }));
    }
  }, [navState.modalTransitionTimeout]);

  // Body overflow durumunu ve scroll olayını dinle
  useEffect(() => {

    // MutationObserver ile body attribute değişikliklerini izle
    const observer = new MutationObserver(checkModalStatus);

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-modal-open', 'data-uploading']
    });

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
      if (navState.modalTransitionTimeout) {
        clearTimeout(navState.modalTransitionTimeout);
      }
    };
  }, [handleScroll, checkModalStatus, navState.modalTransitionTimeout]);
  
  // Sayfa yüklendiğinde de boşluk kontrolü yap
  useEffect(() => {
    const st = window.pageYOffset || document.documentElement.scrollTop;
    const backToTopThreshold = 300;
    setScrollState({
      lastKnownScrollPosition: st,
      shouldHaveBottomPadding: st > backToTopThreshold
    });
  }, []);

  // Sidebar açılıp/kapandığında metinleri göster/gizle
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (uiState.sidebarOpen && !uiState.isMobile) {
      timer = setTimeout(() => {
        setUiState(prev => ({ ...prev, showSidebarText: true }));
      }, 150);
    } else if (!uiState.sidebarOpen && !uiState.isMobile) {
      setUiState(prev => ({ ...prev, showSidebarText: false }));
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [uiState.sidebarOpen, uiState.isMobile]);

  // Overlay, body scroll ve menü düğmesi yönetimi
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (uiState.sidebarOpen && uiState.isMobile) {
      // Sidebar açılıyor (mobil): body scroll'u kilitle, overlay'i göster ve menü düğmesini gizle
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      setUiState(prev => ({ ...prev, showOverlay: true, showMenuButton: false }));
    } else if (!uiState.sidebarOpen && uiState.isMobile) {
      // Sidebar kapanıyor (mobil): overlay'i hemen gizle
      setUiState(prev => ({ ...prev, showOverlay: false }));

      // Animasyon bittikten sonra body scroll'u aç ve menü düğmesinin durumunu ayarla
      timer = setTimeout(() => {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';

            // Modal açık değilse ve yükleme yapılmıyorsa, scroll pozisyonuna göre menü düğmesini göster
            const isModalOpen = document.body.hasAttribute('data-modal-open');
            const isUploading = document.body.hasAttribute('data-uploading');
            if (!isModalOpen && !isUploading) {
              const st = window.pageYOffset || document.documentElement.scrollTop;
              const threshold = Math.max(100, window.innerHeight * 0.15);
              setUiState(prev => ({ ...prev, showMenuButton: st <= threshold }));
            }
      }, 300); // 300ms, sidebar kapanma animasyonunun süresi
    } else {
      // Masaüstü veya mobil değilse: her şeyi varsayılan duruma getir
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
      setUiState(prev => ({ ...prev, showOverlay: false }));
    }

    return () => {
      if (timer) clearTimeout(timer);
      // Temizlik: Eğer component unmount olurken body kilitliyse açalım.
      // Bu, sayfa değiştirme gibi durumlarda takılı kalmasını önler.
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
      }
    };
  }, [uiState.sidebarOpen, uiState.isMobile]);
  
  // Menü düğmesi görünürlüğü değiştiğinde MenuContext'i güncelle
  useEffect(() => {
    setIsMenuButtonVisible(uiState.showMenuButton);
  }, [uiState.showMenuButton, setIsMenuButtonVisible]);

  // Location değiştiğinde navigation state'i tamamen sıfırla (sadece mobilde)
  useEffect(() => {
    // Sadece mobil cihazlarda navigation state'i sıfırla
    if (uiState.isMobile) {
      setNavState({
        clickedPath: null,
        modalTransitionTimeout: null
      });
    }
  }, [sidebarKey, uiState.isMobile]); // Sadece mobilde sıfırla

  return (
    <div className="min-h-screen-mobile flex bg-gray-50 dark:bg-gray-900 lg:max-w-6xl lg:mx-auto lg:shadow-2xl">
      {/* Overlay - sürekli DOM'da kalır; animasyon bitince opaklığın sıfırlanıp beyaz flaş vermesini engeller */}
      {uiState.isMobile && (
        <div
          className={`sidebar-overlay fixed inset-0 bg-gray-900/50 z-10 ${uiState.showOverlay ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={() => setUiState(prev => ({ ...prev, sidebarOpen: false }))}
        />
      )}

      {/* Sidebar */}
      <div className={sidebarClassName}>
        <div className="p-6 border-b border-white/20 dark:border-gray-700/30 flex items-center justify-between">
          <div className={`${uiState.sidebarOpen || uiState.isMobile ? 'flex' : 'hidden'} items-center`}>
            <img 
              src="/bolat-logo-part-1.png" 
              alt="Bolat Logo Part 1" 
              className={`${uiState.isMobile ? 'h-9' : 'h-10'} w-auto`}
            />
            <img 
              src="/bolat-logo-part-2.png" 
              alt="Bolat Logo Part 2" 
              className={`${uiState.isMobile ? 'h-9' : 'h-10'} w-auto dark:brightness-0 dark:invert dark:opacity-80`}
            />
          </div>
          <button
            onClick={handleSidebarToggle}
            className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 ${uiState.isMobile ? '' : 'transition-colors duration-200'} ${uiState.isMobile ? 'border border-gray-300 dark:border-gray-600' : ''} ${!uiState.sidebarOpen && !uiState.isMobile ? 'mx-auto' : ''}`}
          >
            <svg 
              className={`w-5 h-5 transition-all duration-300 ${
                uiState.sidebarOpen ? 'rotate-180 scale-110' : 'rotate-0 scale-100'
              }`} 
              style={{
                transitionDelay: uiState.sidebarOpen ? '200ms' : '0ms',
                transitionTimingFunction: uiState.sidebarOpen 
                  ? 'cubic-bezier(0.34, 1.56, 0.64, 1)' // Bounce out effect when opening
                  : 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' // Smooth ease when closing
              }}
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            </svg>
          </button>
        </div>

                         <div className="flex-1 overflow-y-auto">
          <nav className={`${uiState.sidebarOpen ? 'px-6' : 'px-4'} py-6 ${uiState.isMobile ? '' : 'transition-[padding] duration-300'} h-full flex flex-col`}>
            <div className={`
              ${((uiState.sidebarOpen && uiState.showSidebarText) || uiState.isMobile) ? 'opacity-100 translate-x-0 w-auto' : 'opacity-0 -translate-x-2 w-0 overflow-hidden'} 
              ${!uiState.sidebarOpen && !uiState.isMobile && !uiState.showSidebarText ? 'hidden' : ''}
              ${uiState.isMobile ? '' : 'transition-[opacity,transform,width] duration-300 ease-in-out'} mb-6
            `}>
              <div className={`border ${uiState.isMobile ? 'border-gray-300 dark:border-gray-600' : 'border-gray-200 dark:border-gray-700'} rounded-xl bg-gradient-to-br ${uiState.isMobile ? 'from-white via-gray-50 to-gray-100' : 'from-white via-white to-gray-100'} dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-700 backdrop-blur-sm`}>
                <div className="rounded-xl p-4 border border-white dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 p-2 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Hoş Geldiniz</p>
                    <p className="text-lg font-bold text-gray-900 dark:text-white truncate">{userData?.UserName}</p>
                  </div>
                </div>
                </div>
              </div>
            </div>

            <ul className="space-y-3 flex flex-col h-full">
              {/* Navigation Items */}
              {navigationItems.map((item) => (
                <NavigationItem
                  key={uiState.isMobile ? `${item.id}-${sidebarKey}` : item.id}
                  item={item}
                  isActive={isActive(item.path)}
                  onNavigate={handleNavigation}
                  ripples={ripples}
                  sidebarOpen={uiState.sidebarOpen}
                  isMobile={uiState.isMobile}
                  showSidebarText={uiState.showSidebarText}
                    />
                  ))}
              
              {/* Spacer - alt düğmeleri aşağı iter */}
              <li className="flex-1"></li>

              <li className="border-t border-white/20 dark:border-gray-700/30">
                <button
                  onClick={handleNavigation('service-bulletins', '/service-bulletins')}
                  aria-label="Servis Bültenleri"
                  className={`group relative flex items-center w-full rounded-xl ${uiState.isMobile ? '' : 'transition-colors duration-200'} overflow-hidden border text-left ${
                    isActive('/service-bulletins')
                      ? 'border-blue-200 dark:border-blue-700 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 text-blue-700 dark:text-blue-300 shadow-sm'
                      : `border ${uiState.isMobile ? 'border-gray-300 dark:border-gray-600' : 'border-gray-200 dark:border-gray-700'} bg-gradient-to-br ${uiState.isMobile ? 'from-gray-50 to-gray-150' : 'from-white to-gray-50'} dark:from-gray-800 dark:to-gray-700 text-gray-700 dark:text-gray-300 hover:from-blue-50 hover:to-indigo-100 dark:hover:from-blue-900/20 dark:hover:to-indigo-900/20 hover:text-blue-700 dark:hover:text-blue-300 hover:border-blue-200 dark:hover:border-blue-700 shadow-sm hover:shadow-md transition-all duration-200`
                  }`}
                >
                  <div className={`rounded-xl ${uiState.sidebarOpen || uiState.isMobile ? 'p-3' : 'p-2 justify-center'} w-full flex items-center border border-white dark:border-gray-700`}>
                    <div className={`flex-shrink-0 p-1.5 rounded-lg ${uiState.isMobile ? '' : 'transition-colors duration-200'} ${
                      isActive('/service-bulletins')
                        ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white'
                        : 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-600 dark:text-gray-400 group-hover:from-blue-500 group-hover:to-indigo-600 group-hover:text-white'
                    }`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                      </svg>
                    </div>
                    <span className={`
                      ${((uiState.sidebarOpen && uiState.showSidebarText) || uiState.isMobile) ? 'opacity-100 translate-x-0 w-auto ml-3' : 'opacity-0 -translate-x-2 w-0 overflow-hidden ml-0'}
                      ${uiState.isMobile ? '' : 'transition-[opacity,transform,width,margin] duration-300 ease-in-out'} whitespace-nowrap font-medium
                    `}>
                      Servis Bültenleri
                    </span>
                    {((uiState.sidebarOpen && uiState.showSidebarText) || uiState.isMobile) && (
                      <div className={`flex-shrink-0 ml-auto ${isActive('/service-bulletins') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400'}`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  {ripples['service-bulletins']?.map((ripple) => (
                    <motion.div
                      key={ripple.id}
                      className="absolute rounded-full bg-blue-500/20 pointer-events-none"
                      style={{
                        left: ripple.x - (ripple.size || 50) / 2,
                        top: ripple.y - (ripple.size || 50) / 2,
                        width: ripple.size || 50,
                        height: ripple.size || 50,
                      }}
                      initial={{ scale: 0.1, opacity: 0.6 }}
                      animate={{ scale: 1, opacity: 0 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    />
                  ))}
                </button>
              </li>
              
              {/* Karanlık Mod düğmesi */}
              <li>
                <button
                  onClick={handleThemeToggle()}
                  className={`group relative flex items-center w-full rounded-xl ${uiState.isMobile ? '' : 'transition-colors duration-200'} overflow-hidden border ${uiState.isMobile ? 'border-gray-300 dark:border-gray-600' : 'border-gray-200 dark:border-gray-700'} bg-gradient-to-br ${uiState.isMobile ? 'from-gray-50 to-gray-150' : 'from-white to-gray-50'} dark:from-gray-800 dark:to-gray-700 text-gray-700 dark:text-gray-300 hover:from-blue-50 hover:to-indigo-100 dark:hover:from-blue-900/20 dark:hover:to-indigo-900/20 hover:text-blue-700 dark:hover:text-blue-300 hover:border-blue-200 dark:hover:border-blue-700 shadow-sm hover:shadow-md transition-all duration-200 text-left`}
                >
                  <div className={`rounded-xl ${uiState.sidebarOpen || uiState.isMobile ? 'p-3' : 'p-2 justify-center'} w-full flex items-center border border-white dark:border-gray-700`}>
                  <div className={`flex-shrink-0 p-1.5 rounded-lg ${uiState.isMobile ? '' : 'transition-colors duration-200'} bg-gradient-to-br ${uiState.isMobile ? 'from-gray-100 to-gray-200' : 'from-gray-100 to-gray-200'} dark:from-gray-700 dark:to-gray-600 text-gray-600 dark:text-gray-400 group-hover:from-blue-500 group-hover:to-indigo-600 group-hover:text-white`}>
                    {theme === 'light' ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                      </svg>
                    ) : theme === 'dark' ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    )}
                  </div>
                  <span className={`
                    ${((uiState.sidebarOpen && uiState.showSidebarText) || uiState.isMobile) ? 'opacity-100 translate-x-0 w-auto ml-3' : 'opacity-0 -translate-x-2 w-0 overflow-hidden ml-0'} 
                    ${uiState.isMobile ? '' : 'transition-[opacity,transform,width,margin] duration-300 ease-in-out'} whitespace-nowrap font-medium
                  `}>
                    {theme === 'light' ? 'Karanlık Mod' : theme === 'dark' ? 'Sistem Teması' : 'Aydınlık Mod'}
                  </span>
                  {((uiState.sidebarOpen && uiState.showSidebarText) || uiState.isMobile) && (
                    <div className="flex-shrink-0 ml-auto ${uiState.isMobile ? '' : 'transition-colors duration-200'} text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                      <svg className="w-4 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                  
                  {/* Ripple Effects */}
                  </div>
                  {ripples['theme']?.map((ripple) => (
                    <motion.div
                      key={ripple.id}
                      className="absolute rounded-full bg-blue-500/20 pointer-events-none"
                      style={{
                        left: ripple.x - (ripple.size || 50) / 2,
                        top: ripple.y - (ripple.size || 50) / 2,
                        width: ripple.size || 50,
                        height: ripple.size || 50,
                      }}
                      initial={{ scale: 0.1, opacity: 0.6 }}
                      animate={{ scale: 1, opacity: 0 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    />
                  ))}
                </button>
              </li>
              
              {/* Çıkış Yap düğmesi */}
              <li>
                <button
                  onClick={handleLogoutClick()}
                  className={`group relative flex items-center w-full rounded-xl ${uiState.isMobile ? '' : 'transition-colors duration-200'} overflow-hidden border ${uiState.isMobile ? 'border-red-300 dark:border-red-600' : 'border-red-200 dark:border-red-700'} bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800 text-red-600 dark:text-red-400 hover:from-red-100 hover:to-red-200 dark:hover:from-red-800 dark:hover:to-red-700 hover:text-red-700 dark:hover:text-red-300 shadow-sm hover:shadow-md transition-all duration-200 text-left`}
                >
                  <div className={`rounded-xl ${uiState.sidebarOpen || uiState.isMobile ? 'p-3' : 'p-2 justify-center'} w-full flex items-center border border-white dark:border-gray-700`}>
                  <div className="flex-shrink-0 p-1.5 rounded-lg ${uiState.isMobile ? '' : 'transition-colors duration-200'} bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/40 dark:to-red-800 text-red-600 dark:text-red-400 group-hover:from-red-500 group-hover:to-red-600 group-hover:text-white">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </div>
                  <span className={`
                    ${((uiState.sidebarOpen && uiState.showSidebarText) || uiState.isMobile) ? 'opacity-100 translate-x-0 w-auto ml-3' : 'opacity-0 -translate-x-2 w-0 overflow-hidden ml-0'} 
                    ${uiState.isMobile ? '' : 'transition-[opacity,transform,width,margin] duration-300 ease-in-out'} whitespace-nowrap font-medium
                  `}>
                    Çıkış Yap
                  </span>
                  {((uiState.sidebarOpen && uiState.showSidebarText) || uiState.isMobile) && (
                                          <div className="flex-shrink-0 ml-auto ${uiState.isMobile ? '' : 'transition-colors duration-200'} text-red-400 group-hover:text-red-600 dark:group-hover:text-red-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                  
                  {/* Ripple Effects */}
                  </div>
                  {ripples['logout']?.map((ripple) => (
                    <motion.div
                      key={ripple.id}
                      className="absolute rounded-full bg-red-500/20 pointer-events-none"
                      style={{
                        left: ripple.x - (ripple.size || 50) / 2,
                        top: ripple.y - (ripple.size || 50) / 2,
                        width: ripple.size || 50,
                        height: ripple.size || 50,
                      }}
                      initial={{ scale: 0.1, opacity: 0.6 }}
                      animate={{ scale: 1, opacity: 0 }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                    />
                  ))}
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="transition-[width] duration-300 ease-in-out flex-1 min-w-0 bg-gray-50 dark:bg-gray-900">
        {/* Mobil menu butonu */}
        {uiState.isMobile && !uiState.sidebarOpen && (
          <button
            onClick={() => {
              // Modal açıkken veya yükleme sırasında sidebar açılmasını engelle
              const isModalOpen = document.body.hasAttribute('data-modal-open');
              const isUploading = document.body.hasAttribute('data-uploading');
              if (isModalOpen || isUploading) return;
              
              setUiState(prev => ({ ...prev, sidebarOpen: true }));
            }}
            className={menuButtonClassName}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
        
        {/* Sayfa içeriği - Swipe to open sidebar */}
        <main 
          className={`p-6 ${(scrollState.shouldHaveBottomPadding || isBackToTopVisible) ? 'pb-24 md:pb-6' : 'pb-6'}`}
          onTouchStart={useCallback((e: React.TouchEvent) => {
            if (!uiState.isMobile || uiState.sidebarOpen) return;
            
            // Modal açıkken veya yükleme sırasında swipe gesture'ı engelle
            const isModalOpen = document.body.hasAttribute('data-modal-open');
            const isUploading = document.body.hasAttribute('data-uploading');
            if (isModalOpen || isUploading) return;
            
            const touch = e.touches[0];
            const startX = touch.clientX;
            const startY = touch.clientY;
            
            const handleTouchMove = (e: TouchEvent) => {
              const touch = e.touches[0];
              const currentX = touch.clientX;
              const currentY = touch.clientY;
              
              const deltaX = currentX - startX;
              const deltaY = Math.abs(currentY - startY);
              
              if (deltaX > 50 && deltaY < 100 && deltaX > deltaY) {
                setUiState(prev => ({ ...prev, sidebarOpen: true }));
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
              }
            };
            
            const handleTouchEnd = () => {
              document.removeEventListener('touchmove', handleTouchMove);
              document.removeEventListener('touchend', handleTouchEnd);
            };
            
            document.addEventListener('touchmove', handleTouchMove, { passive: true });
            document.addEventListener('touchend', handleTouchEnd);
          }, [uiState.isMobile, uiState.sidebarOpen])}
        >
          {children}
        </main>
      </div>
    </div>
  );
} 